// Landing page publique — pas d'auth requise.
// Route : /c/:slug (montée hors du AppShell).
// Mobile-first, formulaire minimal, consentement explicite obligatoire.
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";

const NAVY = "#0B2545";
const IVORY = "#FBF6EA";
const CHAMPAGNE = "#D8C7A0";
const GOLD = "#C9A227";
const SLATE = "#64748B";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function CampaignLanding() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    city: "", region: "", extra_answer: "", consent: false,
  });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(
          `${BASE}/api/leads-engine/public/campaigns/${slug}`);
        if (!cancel) setCampaign(r.data);
      } catch (err) {
        if (!cancel) setError(err?.response?.data?.detail
                                       || "Campagne introuvable.");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [slug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Prénom et nom requis."); return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Fournissez au moins un email ou un téléphone."); return;
    }
    if (!form.consent) {
      toast.error("Vous devez cocher le consentement."); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        utm_source:   search.get("utm_source"),
        utm_medium:   search.get("utm_medium"),
        utm_campaign: search.get("utm_campaign"),
        utm_content:  search.get("utm_content"),
        utm_term:     search.get("utm_term"),
      };
      const r = await axios.post(
        `${BASE}/api/leads-engine/public/campaigns/${slug}/submit`,
        payload);
      setSubmitted(r.data?.success_message
                            || "Merci ! Vous serez recontacté rapidement.");
    } catch (err) {
      toast.error(err?.response?.data?.detail
                    || "Une erreur est survenue.");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: IVORY,
                    display: "grid", placeItems: "center", color: NAVY,
                    fontFamily: "ui-sans-serif, system-ui" }}
           data-testid="landing-loading">
        Chargement…
      </div>);
  }
  if (error || !campaign) {
    return (
      <div style={{ minHeight: "100vh", background: IVORY,
                    display: "grid", placeItems: "center", color: NAVY,
                    padding: 24, fontFamily: "ui-sans-serif, system-ui" }}
           data-testid="landing-error">
        <div style={{ maxWidth: 460, textAlign: "center" }}>
          <h1 style={{ fontSize: 22 }}>Campagne indisponible</h1>
          <p style={{ marginTop: 12, color: SLATE, fontSize: 14 }}>
            {error || "Cette campagne n'est plus active."}
          </p>
        </div>
      </div>);
  }
  if (submitted) {
    return (
      <div style={{ minHeight: "100vh", background: IVORY, color: NAVY,
                    display: "grid", placeItems: "center", padding: 24,
                    fontFamily: "ui-sans-serif, system-ui" }}
           data-testid="landing-success">
        <div style={{ maxWidth: 460, textAlign: "center" }}>
          <CheckCircle2 style={{ width: 48, height: 48, color: GOLD,
                                  margin: "0 auto 16px" }} />
          <h1 style={{ fontSize: 24, fontFamily: "'DM Serif Display', serif" }}>
            {submitted}
          </h1>
          <p style={{ marginTop: 12, color: SLATE, fontSize: 14 }}>
            Vos coordonnées sont conservées en toute confidentialité,
            conformément à la Loi 25 (Québec).
          </p>
        </div>
      </div>);
  }

  return (
    <div style={{ minHeight: "100vh", background: IVORY, color: NAVY,
                  fontFamily: "ui-sans-serif, system-ui" }}
         data-testid="landing-root">
      <div style={{ maxWidth: 560, margin: "0 auto",
                    padding: "48px 20px 80px" }}>
        <div style={{ fontSize: 10, letterSpacing: "0.28em",
                      textTransform: "uppercase", color: GOLD,
                      fontFamily: "ui-monospace, SFMono-Regular" }}>
          ALTIORA · Campagne
        </div>
        <h1 style={{ fontSize: 32, lineHeight: 1.15, marginTop: 12,
                     fontFamily: "'DM Serif Display', ui-serif, serif" }}
            data-testid="landing-title">
          {campaign.name}
        </h1>
        {campaign.sub_headline && (
          <p style={{ marginTop: 8, color: SLATE, fontSize: 15 }}>
            {campaign.sub_headline}
          </p>
        )}
        <p style={{ marginTop: 16, fontSize: 15, lineHeight: 1.55 }}
           data-testid="landing-value-prop">
          {campaign.value_prop}
        </p>

        <form onSubmit={submit} data-testid="landing-form"
              style={{ marginTop: 32, background: "white",
                       border: `1px solid ${CHAMPAGNE}55`,
                       borderRadius: 12, padding: 20,
                       display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gap: 14,
                          gridTemplateColumns: "1fr 1fr" }}>
            <Field label="Prénom *" testid="landing-first-name"
                   value={form.first_name}
                   onChange={(v) => set("first_name", v)} required />
            <Field label="Nom *" testid="landing-last-name"
                   value={form.last_name}
                   onChange={(v) => set("last_name", v)} required />
          </div>
          {campaign.ask_email && (
            <Field label="Courriel" type="email"
                   testid="landing-email"
                   value={form.email} onChange={(v) => set("email", v)} />
          )}
          {campaign.ask_phone && (
            <Field label="Téléphone" type="tel"
                   testid="landing-phone"
                   value={form.phone} onChange={(v) => set("phone", v)} />
          )}
          {campaign.ask_city && (
            <div style={{ display: "grid", gap: 14,
                            gridTemplateColumns: "2fr 1fr" }}>
              <Field label="Ville" testid="landing-city"
                     value={form.city}
                     onChange={(v) => set("city", v)} />
              <Field label="Province" testid="landing-region"
                     value={form.region}
                     onChange={(v) => set("region", v)}
                     placeholder="QC" />
            </div>
          )}
          {campaign.extra_question && (
            <Field label={campaign.extra_question}
                   testid="landing-extra"
                   value={form.extra_answer}
                   onChange={(v) => set("extra_answer", v)} />
          )}

          <label style={{ display: "flex", gap: 10, alignItems: "flex-start",
                            marginTop: 8, cursor: "pointer",
                            fontSize: 12, color: NAVY }}
                 data-testid="landing-consent-label">
            <input type="checkbox" checked={form.consent}
                   onChange={(e) => set("consent", e.target.checked)}
                   data-testid="landing-consent-checkbox"
                   style={{ marginTop: 3 }} />
            <span>{campaign.consent_text}</span>
          </label>

          <button type="submit" disabled={busy}
                  data-testid="landing-submit"
                  style={{ marginTop: 8, padding: "12px 20px",
                           background: NAVY, color: IVORY, border: 0,
                           borderRadius: 999, fontSize: 14,
                           cursor: busy ? "wait" : "pointer",
                           fontFamily: "inherit" }}>
            {busy ? "Envoi…" : (campaign.cta_label || "Envoyer")}
          </button>

          <p style={{ fontSize: 10, color: SLATE, marginTop: 4,
                        textAlign: "center" }}>
            Vos coordonnées sont utilisées uniquement dans le cadre de
            votre demande, conformément à la Loi 25 (Québec) et à la LCAP.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text",
                    required = false, placeholder = "" }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ fontSize: 10, letterSpacing: "0.22em",
                     textTransform: "uppercase", color: "#64748B",
                     fontFamily: "ui-monospace" }}>{label}</span>
      <input type={type} value={value} required={required}
             placeholder={placeholder}
             data-testid={testid}
             onChange={(e) => onChange(e.target.value)}
             style={{ marginTop: 4, width: "100%", padding: "10px 12px",
                      border: "1px solid #D8C7A055", borderRadius: 8,
                      fontSize: 14, color: "#0B2545",
                      background: "#FBF6EA", fontFamily: "inherit" }} />
    </label>
  );
}
